import { supabase } from '../lib/supabase';
import { Player } from '../types';

export interface ClubMembership {
  club_id: number;
  club_name: string;
  role: string;
  joined_at: string;
}

export interface UserLicense {
  license_type: string;
  expires_at?: string;
}

export interface UserManagementData extends Player {
  last_login?: string;
  total_games?: number;
  total_clubs?: number;
  club_memberships?: ClubMembership[];
  license?: UserLicense;
  status: 'active' | 'deleted';
}

export interface GuestPlayer extends Player {
  is_guest: true;
  total_games?: number;
}

// Get all users with optional filters
export const getAllUsers = async (includeDeleted: boolean = false): Promise<UserManagementData[]> => {
  try {
    // Start with basic player data
    let query = supabase
      .from('players')
      .select(`
        *,
        games_player_a:games!playerA(count),
        games_player_b:games!playerB(count)
      `);

    // Filter out deleted users by default
    if (!includeDeleted) {
      query = query.is('deleted_at', null);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error fetching users:', error);
      throw error;
    }

    // Transform the data to include computed fields
    const transformedData: UserManagementData[] = (data || []).map(player => {
      // Set default values for club and license data
      const clubMemberships: ClubMembership[] = [];
      const license: UserLicense = { license_type: 'basic' };

      return {
        ...player,
        status: player.deleted_at ? 'deleted' : 'active',
        total_games: (player.games_player_a?.[0]?.count || 0) + (player.games_player_b?.[0]?.count || 0),
        total_clubs: 0, // Will be populated separately if needed
        club_memberships: clubMemberships,
        license: license
      };
    });

    return transformedData;
  } catch (error) {
    console.error('Error in getAllUsers:', error);
    // Return empty array instead of throwing to prevent loading issues
    return [];
  }
};

// Get user by ID with detailed information
export const getUserById = async (userId: number): Promise<UserManagementData | null> => {
  const { data, error } = await supabase
    .from('players')
    .select(`
      *,
      games_player_a:games!playerA(count),
      games_player_b:games!playerB(count)
    `)
    .eq('id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // User not found
    }
    console.error('Error fetching user:', error);
    throw error;
  }

  return {
    ...data,
    status: data.deleted_at ? 'deleted' : 'active',
    total_games: (data.games_player_a?.[0]?.count || 0) + (data.games_player_b?.[0]?.count || 0)
  };
};

// Update user profile information
export const updateUserProfile = async (userId: number, updates: Partial<Player>): Promise<Player> => {
  const { data, error } = await supabase
    .from('players')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }

  return data;
};

// Toggle soft delete status
export const toggleUserStatus = async (userId: number, userUuid: string | null, isCurrentlyDeleted: boolean): Promise<boolean> => {
  // For guest players (no UUID), use direct update
  if (!userUuid) {
    const { error } = await supabase
      .from('players')
      .update({ 
        deleted_at: isCurrentlyDeleted ? null : new Date().toISOString() 
      })
      .eq('id', userId);
    
    if (error) {
      console.error('Error toggling guest player status:', error);
      throw error;
    }
    
    return true;
  }
  
  // For regular users with UUID, use RPC functions
  if (isCurrentlyDeleted) {
    // Restore user
    const { data, error } = await supabase.rpc('restore_player', {
      p_user_uuid: userUuid
    });
    
    if (error) {
      console.error('Error restoring user:', error);
      throw error;
    }
    
    return data;
  } else {
    // Soft delete user
    const { data, error } = await supabase.rpc('soft_delete_player', {
      p_user_uuid: userUuid
    });
    
    if (error) {
      console.error('Error soft deleting user:', error);
      throw error;
    }
    
    return data;
  }
};

// Trigger password reset for user
export const triggerPasswordReset = async (email: string): Promise<void> => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`
  });

  if (error) {
    console.error('Error triggering password reset:', error);
    throw error;
  }
};

// Search users by name or email
export const searchUsers = async (searchTerm: string, includeDeleted: boolean = false): Promise<UserManagementData[]> => {
  try {
    let query = supabase
      .from('players')
      .select(`
        *,
        games_player_a:games!playerA(count),
        games_player_b:games!playerB(count)
      `)
      .or(`name.ilike.%${searchTerm}%,preName.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);

    // Filter out deleted users by default
    if (!includeDeleted) {
      query = query.is('deleted_at', null);
    }

    const { data, error } = await query.order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error searching users:', error);
      throw error;
    }

    // Transform the data to include computed fields
    const transformedData: UserManagementData[] = (data || []).map(player => {
      // Set default values for club and license data
      const clubMemberships: ClubMembership[] = [];
      const license: UserLicense = { license_type: 'basic' };

      return {
        ...player,
        status: player.deleted_at ? 'deleted' : 'active',
        total_games: (player.games_player_a?.[0]?.count || 0) + (player.games_player_b?.[0]?.count || 0),
        total_clubs: 0, // Will be populated separately if needed
        club_memberships: clubMemberships,
        license: license
      };
    });

    return transformedData;
  } catch (error) {
    console.error('Error in searchUsers:', error);
    // Return empty array instead of throwing to prevent loading issues
    return [];
  }
};

// Load club memberships for a specific user
export const getUserClubMemberships = async (playerId: number): Promise<ClubMembership[]> => {
  try {
    const { data, error } = await supabase
      .from('club_members')
      .select(`
        club_id,
        role,
        joined_at,
        clubs(name)
      `)
      .eq('player_id', playerId);

    if (error) {
      console.error('Error fetching club memberships:', error);
      return [];
    }

    return (data || []).map((membership: any) => ({
      club_id: membership.club_id,
      club_name: membership.clubs?.name || 'Unknown Club',
      role: membership.role,
      joined_at: membership.joined_at
    }));
  } catch (error) {
    console.error('Error in getUserClubMemberships:', error);
    return [];
  }
};

// Load user license information
export const getUserLicense = async (playerId: number): Promise<UserLicense> => {
  try {
    // First get the player's UUID
    const { data: playerData, error: playerError } = await supabase
      .from('players')
      .select('playerUUID')
      .eq('id', playerId)
      .single();

    if (playerError || !playerData) {
      console.error('Error fetching player UUID:', playerError);
      return { license_type: 'basic' };
    }

    const { data, error } = await supabase
      .from('user_licenses')
      .select('license_type, expires_at')
      .eq('user_id', playerData.playerUUID)
      .limit(1);

    if (error) {
      console.error('Error fetching user license:', error);
      return { license_type: 'basic' };
    }

    if (data && data.length > 0) {
      return {
        license_type: data[0].license_type,
        expires_at: data[0].expires_at
      };
    }

    return { license_type: 'basic' };
  } catch (error) {
    console.error('Error in getUserLicense:', error);
    return { license_type: 'basic' };
  }
};

// Update user license
export const updateUserLicense = async (playerId: number, licenseType: string, expiresAt?: string): Promise<boolean> => {
  try {
    // First get the player's UUID
    const { data: playerData, error: playerError } = await supabase
      .from('players')
      .select('playerUUID')
      .eq('id', playerId)
      .single();

    if (playerError || !playerData) {
      console.error('Error fetching player UUID:', playerError);
      throw playerError;
    }

    // Check if user already has a license
    const { data: existingLicense, error: fetchError } = await supabase
      .from('user_licenses')
      .select('id')
      .eq('user_id', playerData.playerUUID)
      .limit(1);

    if (fetchError) {
      console.error('Error checking existing license:', fetchError);
      throw fetchError;
    }

    if (existingLicense && existingLicense.length > 0) {
      // Update existing license
      const { error: updateError } = await supabase
        .from('user_licenses')
        .update({
          license_type: licenseType,
          expires_at: expiresAt || null
        })
        .eq('user_id', playerData.playerUUID);

      if (updateError) {
        console.error('Error updating user license:', updateError);
        throw updateError;
      }
    } else {
      // Create new license
      const { error: insertError } = await supabase
        .from('user_licenses')
        .insert({
          user_id: playerData.playerUUID,
          license_type: licenseType,
          expires_at: expiresAt || null
        });

      if (insertError) {
        console.error('Error creating user license:', insertError);
        throw insertError;
      }
    }

    return true;
  } catch (error) {
    console.error('Error in updateUserLicense:', error);
    throw error;
  }
};

// Remove user license (set to basic)
export const removeUserLicense = async (playerId: number): Promise<boolean> => {
  try {
    // First get the player's UUID
    const { data: playerData, error: playerError } = await supabase
      .from('players')
      .select('playerUUID')
      .eq('id', playerId)
      .single();

    if (playerError || !playerData) {
      console.error('Error fetching player UUID:', playerError);
      throw playerError;
    }

    const { error } = await supabase
      .from('user_licenses')
      .delete()
      .eq('user_id', playerData.playerUUID);

    if (error) {
      console.error('Error removing user license:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in removeUserLicense:', error);
    throw error;
  }
};

// Get all guest players
export const getAllGuestPlayers = async (): Promise<GuestPlayer[]> => {
  try {
    const { data, error } = await supabase
      .from('players')
      .select(`
        *,
        games_player_a:games!playerA(count),
        games_player_b:games!playerB(count)
      `)
      .eq('is_guest', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching guest players:', error);
      throw error;
    }

    // Calculate total games for each guest
    const guestPlayers: GuestPlayer[] = (data || []).map(player => ({
      ...player,
      is_guest: true,
      total_games: (player.games_player_a?.[0]?.count || 0) + (player.games_player_b?.[0]?.count || 0)
    }));

    return guestPlayers;
  } catch (error) {
    console.error('Error in getAllGuestPlayers:', error);
    throw error;
  }
};

// Link guest player to user account (Global Admin only)
export const linkGuestToUser = async (
  guestPlayerId: number,
  userUuid: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    // Get current user (must be global admin)
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Call the database function
    const { data, error } = await supabase.rpc('link_guest_to_user', {
      p_guest_player_id: guestPlayerId,
      p_user_uuid: userUuid,
      p_admin_uuid: user.id
    });

    if (error) {
      console.error('Error linking guest to user:', error);
      throw error;
    }

    if (data && !data.success) {
      return {
        success: false,
        error: data.error
      };
    }

    return {
      success: true,
      message: data.message
    };
  } catch (error) {
    console.error('Error in linkGuestToUser:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to link guest to user'
    };
  }
};
